import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

/**
 * AddressAutocomplete — one shared control used everywhere a customer/lead/
 * property address is entered.
 *
 * - Debounced Mapbox forward-geocoding (US-biased by default).
 * - Keyboard nav (Up/Down/Enter/Escape) + mouse/touch.
 * - Manual entry always allowed — the input stays editable regardless of
 *   whether a suggestion has been picked, so users are never blocked by a
 *   missing match.
 * - On select emits a rich payload:
 *   `{ formatted, street, city, state, postcode, country, lat, lng }`
 *   Callers decide which fields to write back into their form.
 *
 * The underlying Mapbox token is the same one that powers the satellite
 * measurement tools — no additional integration/key is required.
 */
export default function AddressAutocomplete({
  value = "",
  onChange,
  onSelect,
  placeholder = "Start typing an address…",
  disabled = false,
  countries = "us",
  types = "address,poi",
  proximity = null, // optional { lat, lng } to bias results toward
  className = "",
  inputClassName = "",
  "data-testid": testId,
}) {
  const [text, setText] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const abortRef = useRef(null);
  // Keep local text in sync when parent replaces the value externally.
  useEffect(() => { setText(value || ""); }, [value]);

  // Click-outside closes the dropdown without wiping the input.
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const proximityParam = useMemo(() => (
    proximity && Number.isFinite(proximity.lng) && Number.isFinite(proximity.lat)
      ? `${proximity.lng},${proximity.lat}`
      : ""
  ), [proximity]);

  const runSearch = (q) => {
    if (!MAPBOX_TOKEN || !q || q.trim().length < 3) {
      setSuggestions([]); setOpen(false); setLoading(false); return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    const params = new URLSearchParams({
      access_token: MAPBOX_TOKEN,
      autocomplete: "true",
      limit: "5",
      types,
    });
    if (countries) params.set("country", countries);
    if (proximityParam) params.set("proximity", proximityParam);
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?${params.toString()}`;
    fetch(url, { signal: ctrl.signal })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`http_${r.status}`)))
      .then((j) => {
        setSuggestions(j.features || []);
        setOpen(true);
        setActiveIdx(-1);
      })
      .catch((e) => { if (e.name !== "AbortError") { setSuggestions([]); setOpen(false); } })
      .finally(() => setLoading(false));
  };

  const handleChange = (v) => {
    setText(v);
    onChange?.(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v), 300);
  };

  // Parse Mapbox `context` into { city, state, postcode, country }.
  const parseFeature = (feat) => {
    const ctx = feat.context || [];
    const grab = (prefix) => {
      const c = ctx.find((x) => (x.id || "").startsWith(prefix));
      return c ? (c.text || c.short_code || "") : "";
    };
    const state = ctx.find((x) => (x.id || "").startsWith("region"));
    const stateShort = state ? (state.short_code || "").replace(/^us-/i, "").toUpperCase() : "";
    // Mapbox includes house number on address features via `address` on the feature
    const houseNo = feat.address || "";
    const street = feat.place_type?.[0] === "address"
      ? `${houseNo ? houseNo + " " : ""}${feat.text || ""}`.trim()
      : (feat.text || "");
    const [lng, lat] = feat.center || [null, null];
    return {
      formatted: feat.place_name || feat.text || "",
      street,
      city: grab("place"),
      state: stateShort || (state?.text || ""),
      postcode: grab("postcode"),
      country: grab("country"),
      lat, lng,
      mapbox_id: feat.id,
    };
  };

  const pick = (feat) => {
    const parsed = parseFeature(feat);
    setText(parsed.formatted);
    onChange?.(parsed.formatted);
    onSelect?.(parsed);
    setOpen(false);
    setSuggestions([]);
    setActiveIdx(-1);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      if (activeIdx >= 0 && activeIdx < suggestions.length) {
        e.preventDefault();
        pick(suggestions[activeIdx]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`} data-testid={testId ? `${testId}-wrapper` : undefined}>
      <div className="relative">
        <Input
          value={text}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          onKeyDown={onKeyDown}
          className={inputClassName}
          autoComplete="off"
          data-testid={testId}
        />
        {loading && (
          <Loader2 className="w-3.5 h-3.5 text-zinc-400 absolute right-2 top-1/2 -translate-y-1/2 animate-spin pointer-events-none" />
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-zinc-200 bg-white shadow-lg"
          role="listbox"
          data-testid={testId ? `${testId}-listbox` : undefined}
        >
          {suggestions.map((s, i) => (
            <button
              key={s.id || i}
              type="button"
              role="option"
              aria-selected={i === activeIdx}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 ${i === activeIdx ? "bg-orange-50" : "hover:bg-zinc-50"}`}
              data-testid={testId ? `${testId}-option-${i}` : undefined}
            >
              <MapPin className="w-3.5 h-3.5 text-zinc-400 mt-0.5 shrink-0" />
              <span className="min-w-0">
                <span className="block font-medium truncate">{s.text}</span>
                <span className="block text-xs text-zinc-500 truncate">{s.place_name}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
